import { WS_URL, getWebSocketAuthMessage } from '../config/apiConfig';
import { needsTokenRefresh, refreshToken } from '../auth';
import { stashGameSocket } from '../matchmaking/gameSocketHandoff';

const START_MISSION_TIMEOUT_MS = 30000;

/**
 * Start an Explore Unknown bot mission via WebSocket and navigate to the battle route.
 *
 * @param {{
 *   missionIndex: number,
 *   runId: number,
 *   strategyId: string | number,
 *   navigate: (path: string, options?: object) => void,
 *   token?: string,
 *   options?: {
 *     exploreUnknownDevDirectBossMission?: boolean,
 *     exploreUnknownBossRetryAttempt?: number,
 *     exploreUnknownBossRetryDarknessMeter?: { fromPct: number, toPct: number },
 *   },
 * }} params
 * @returns {Promise<{ gameId: string }>}
 */
export async function startExploreUnknownBotMission({
  missionIndex,
  runId,
  strategyId,
  navigate,
  token: initialToken,
  options = {},
}) {
  if (missionIndex == null || !Number.isInteger(Number(missionIndex)) || Number(missionIndex) < 1) {
    throw new Error('startExploreUnknownBotMission: missionIndex is required');
  }
  if (runId == null) {
    throw new Error('startExploreUnknownBotMission: runId is required');
  }
  if (strategyId == null || strategyId === '') {
    throw new Error('startExploreUnknownBotMission: strategyId is required');
  }
  if (typeof navigate !== 'function') {
    throw new Error('startExploreUnknownBotMission: navigate is required');
  }

  let token = initialToken || localStorage.getItem('token');
  if (!token) {
    throw new Error('startExploreUnknownBotMission: token is required');
  }
  if (needsTokenRefresh(token)) {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue) {
      throw new Error('startExploreUnknownBotMission: refresh token is required when auth token needs refresh');
    }
    token = await refreshToken(refreshTokenValue);
    if (!token) {
      throw new Error('startExploreUnknownBotMission: refreshToken returned an empty token');
    }
    localStorage.setItem('token', token);
  }

  return new Promise((resolve, reject) => {
    const socket = new window.WebSocket(WS_URL);
    let settled = false;

    const finish = (err, result, finishOptions = {}) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      if (!finishOptions.preserveSocket) {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      }
      if (err) reject(err);
      else resolve(result);
    };

    const timeoutId = window.setTimeout(() => {
      finish(new Error('startExploreUnknownBotMission: timed out waiting for match-found'));
    }, START_MISSION_TIMEOUT_MS);

    socket.onopen = () => {
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
    };

    socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === 'auth-success') {
        const startPayload = {
          type: 'start-bot-game',
          strategyId: String(strategyId),
          exploreUnknownMissionIndex: Number(missionIndex),
          exploreUnknownRunId: Number(runId),
          developerMode: false,
        };
        if (options.exploreUnknownDevDirectBossMission === true) {
          startPayload.exploreUnknownDevDirectBossMission = true;
        }
        if (options.exploreUnknownBossRetryAttempt != null) {
          startPayload.exploreUnknownBossRetryAttempt = Number(options.exploreUnknownBossRetryAttempt);
        }
        socket.send(JSON.stringify(startPayload));
      }

      if (data.type === 'match-found') {
        if (data.gameId == null || String(data.gameId).trim() === '') {
          finish(new Error('startExploreUnknownBotMission: match-found missing gameId'));
          return;
        }
        const selectedGeneral = localStorage.getItem('selectedGeneral') || '';
        const selectedAdvisor = localStorage.getItem('selectedAdvisor') || '';
        const navState = {
          strategyId: String(strategyId),
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          selectedGeneral,
          selectedAdvisor,
          isBotGame: true,
          gameState: data.gameState,
          yourTurn: data.yourTurn,
          handedOffGameSocket: true,
        };
        const retryAttempt = options.exploreUnknownBossRetryAttempt;
        if (retryAttempt != null) {
          const n = Number(retryAttempt);
          if (!Number.isInteger(n) || n < 2) {
            throw new Error(
              'startExploreUnknownBotMission: exploreUnknownBossRetryAttempt must be an integer >= 2'
            );
          }
          navState.exploreUnknownBossRetryAttempt = n;
        }
        if (options.exploreUnknownBossRetryDarknessMeter != null) {
          const meter = options.exploreUnknownBossRetryDarknessMeter;
          if (
            typeof meter !== 'object' ||
            typeof meter.fromPct !== 'number' ||
            Number.isNaN(meter.fromPct) ||
            typeof meter.toPct !== 'number' ||
            Number.isNaN(meter.toPct)
          ) {
            throw new Error(
              'startExploreUnknownBotMission: exploreUnknownBossRetryDarknessMeter requires numeric fromPct and toPct'
            );
          }
          navState.exploreUnknownBossRetryDarknessMeter = {
            fromPct: meter.fromPct,
            toPct: meter.toPct,
          };
        }
        stashGameSocket(data.gameId, socket);
        navigate(`/battle/${data.gameId}`, {
          replace: true,
          state: navState,
        });
        finish(null, { gameId: data.gameId }, { preserveSocket: true });
      }

      if (data.type === 'error') {
        finish(new Error(data.message || 'startExploreUnknownBotMission: start-bot-game failed'));
      }
    };

    socket.onerror = () => {
      finish(new Error('startExploreUnknownBotMission: WebSocket error'));
    };
  });
}
